import { DateTime } from "luxon";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FieldPath, get, useFormContext } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTemplateResult } from "../lookup/utils";
import { produce } from "immer";
import { RecordStandardGroup } from "@/api/record";
import { SegmentResults, TemplateResults, TestEventResults, WelderResults } from "@/api/williams/testEvents";
import { TestResult } from "@/api/williams/testEvents";
import { TestRow } from "./TestResultsTest";
import { useState } from "react";

import styles from "./TestResults.module.css";

type SegmentSectionProps = {
  welder: WelderResults;
  template: TemplateResults;
  segment: SegmentResults;
  path: FieldPath<TestEventResults>;
  isLocked: boolean;
};

export function SegmentSection({ welder, template, segment, path, isLocked }: SegmentSectionProps) {
  const [expanded, setExpanded] = useState<boolean>(
    template.standard === RecordStandardGroup.ASME || template.segments.length === 1
  );

  const form = useFormContext<TestEventResults>();
  const error = get(form.formState.errors, path);
  const formData = form.watch();

  function handleFieldChanged(updatedValues: Partial<SegmentResults>) {
    const updatedSegment = { ...segment, ...updatedValues };
    form.setValue(path, updatedSegment, {
      shouldDirty: true,
    });
  }

  function handleResultChanged(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = parseInt(e.target.value) as TestResult;
    const updatedSegment = produce(segment, (draft) => {
      draft.result = value;
      draft.tests.forEach((test) => {
        test.result = value === TestResult.Fail ? TestResult.NotConducted : value;
      });
    });

    const updatedWelder = updateWelder(welder, updatedSegment);
    form.setValue(`welders.${formData.welders.indexOf(welder)}`, updatedWelder, {
      shouldDirty: true,
    });

    if (value === TestResult.Fail) {
      setExpanded(true);
    }
  }

  return (
    <>
      {template.standard === RecordStandardGroup.WAPI && (
        <div className="d-flex justify-content-between mt-2">
          <div className="small">
            <strong>{`Test Number ${segment.segmentNumber}`}</strong>

            {segment.tests.length > 0 && (
              <FontAwesomeIcon
                role="button"
                icon={expanded ? faChevronUp : faChevronDown}
                size="sm"
                className="ms-1"
                title="Show/Hide Tests"
                onClick={() => setExpanded((prev) => !prev)}
                fixedWidth={true}
              />
            )}
          </div>

          <div className="d-flex gap-2 text-center">
            <div className={styles.field}>
              <input
                type="date"
                min="1753-01-01"
                max={DateTime.now().toISODate()}
                className={`form-control ${error?.testDate ? "border-danger" : ""}`}
                value={segment.testDate ?? ""}
                disabled={template.submitted || isLocked}
                onChange={(e) => handleFieldChanged({ testDate: e.target.value })}
              />
            </div>

            <div className={styles.field}>
              <select
                value={segment.result}
                disabled={template.submitted || isLocked}
                className={`form-select ${error?.result ? "border-danger" : ""}`}
                onChange={handleResultChanged}
              >
                <option value={TestResult.None}></option>
                <option value={TestResult.Pass}>Acceptable</option>
                <option value={TestResult.NotConducted}>Not Conducted</option>
                <option value={TestResult.Fail}>Failed</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {expanded && (
        <>
          {segment.tests.map((test, index) => (
            <TestRow
              key={test.testResultId}
              test={test}
              welder={welder}
              template={template}
              isLocked={isLocked}
              path={`${path}.tests.${index}` as FieldPath<TestEventResults>}
            />
          ))}

          <div className="ms-1 mt-2 mb-3">
            <input
              type="text"
              maxLength={250}
              placeholder="Comments..."
              className={`form-control ${error?.comment ? "border-danger" : ""}`}
              value={segment.comment ?? ""}
              disabled={template.submitted || isLocked}
              onChange={(e) => handleFieldChanged({ comment: e.target.value })}
            />
          </div>
        </>
      )}
    </>
  );
}

function updateWelder(welder: WelderResults, updatedSegment: SegmentResults): WelderResults {
  return produce(welder, (draft) => {
    for (const template of draft.templates) {
      const segment = template.segments.find((x) => x.segmentId === updatedSegment.segmentId);
      if (segment) {
        segment.result = updatedSegment.result;
        segment.tests = updatedSegment.tests;
        segment.testDate = updatedSegment.testDate;
        template.result = getTemplateResult(template);
        break;
      }
    }
  });
}
